import { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import apiClient from "../../api-client";
import { CustomTextField } from "../../components/CustomTextField";
import { AuthLayout } from "../../components/AuthLayout";
import { Button } from "../../components/Button";
import { FormField } from "../../components/FormField";

export function ResetPassword() {
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getErrorMessage = (err) => {
    const data = err?.response?.data;
    if (data?.token) {
      return Array.isArray(data.token) ? data.token[0] : data.token;
    }
    if (data?.new_password) {
      return Array.isArray(data.new_password) ? data.new_password[0] : data.new_password;
    }
    if (data?.detail) {
      return data.detail;
    }
    return "No se pudo actualizar la contraseña. Inténtalo de nuevo.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!token) {
      setError("El enlace no es válido. Solicita uno nuevo.");
      return;
    }
    if (!newPassword.trim()) {
      setError("La nueva contraseña es requerida");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post("users/password-reset/confirm/", {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      kicker="Recuperar acceso"
      footer={<RouterLink to="/login">Volver a iniciar sesión</RouterLink>}
      title="Crea una nueva contraseña"
      subtitle="Este enlace es válido durante 15 minutos."
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", rowGap: 2 }}>
        <FormField label="Nueva contraseña">
          <Box sx={{ position: "relative" }}>
            <CustomTextField
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Tu nueva contraseña"
              disabled={isLoading}
              sx={{ paddingRight: "40px" }}
            />
            <IconButton
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              sx={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                padding: "4px",
              }}
            >
              {showPassword ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" />
              )}
            </IconButton>
          </Box>
        </FormField>

        <FormField label="Confirmar contraseña">
          <Box sx={{ position: "relative" }}>
            <CustomTextField
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repite la contraseña"
              disabled={isLoading}
              sx={{ paddingRight: "40px" }}
            />
            <IconButton
              aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
              onClick={() => setShowConfirm(!showConfirm)}
              disabled={isLoading}
              sx={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                padding: "4px",
              }}
            >
              {showConfirm ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" />
              )}
            </IconButton>
          </Box>
        </FormField>

        {error && (
          <Alert severity="error">
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {success && (
          <Alert severity="success">
            <Typography variant="body2">
              Tu contraseña se ha actualizado correctamente.
            </Typography>
          </Alert>
        )}

        <Button
          type="submit"
          size="lg"
          fullWidth
          sx={{ mt: 1 }}
          disabled={isLoading}
        >
          {isLoading ? "Actualizando..." : "Guardar contraseña"}
        </Button>
      </Box>
    </AuthLayout>
  );
}
